import { Component, computed, effect, ElementRef, OnInit, ViewChild, ViewContainerRef, inject } from '@angular/core';
import {
  FormGroupDirective,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {MatCheckboxChange, MatCheckboxModule} from '@angular/material/checkbox';
import {ErrorStateMatcher} from '@angular/material/core';
import {MatDialog} from '@angular/material/dialog';

import {PhoneRegionCodeEnum} from '../../../model/enums/phone-region-code.enum';
import {FamilySituationEnum} from '../../../model/enums/family-situation.enum';
import {SexeEnum} from '../../../model/enums/sexe.enum';
import {FormSelectItem} from '../../../model/form/select-item.model';
import {TreeItemFlatNode} from '../../../model/tree/tree-item-flat-node.model';
import {TreeItemNode} from '../../../model/tree/tree-item-node.model';
import {
  GlobalRegisterDTO,
  RegisterEventDTO,
  RegisterResponsableDTO,
  RegisterYoungDTO
} from '../../../model/dto/simple-inscription.dto';
import {PlaceEventDTO} from '../../../model/dto/place-event.dto';
import {Program} from '../../../model/event/program.model';
import {User} from '../../../model/user/user.model';
import {Young} from '../../../model/user/young.model';
import {Address} from '../../../model/event/place/address.model';
import {RelationUser} from '../../../model/user/relation-user.model';
import {Event} from '../../../model/event/event.model';

import {FormatService} from '../../../services/format.service';
import {SearchWebservice} from '../../../services/webservices/search.webservice';
import {ProgramWebservice} from '../../../services/webservices/program.webservice';
import {EventWebservice} from '../../../services/webservices/event.webservice';
import {UserWebservice} from '../../../services/webservices/user.webservice';
import {TreeDataService} from '../../../services/tree-data.service';
import {TreeChoiceService} from '../../../services/tree-choice.service';
import {StepperService} from '../../../services/stepper.service';
import {AlertService} from '../../../services/alert.service';
import {ValidationService} from '../../../services/validation.service';
import {YoungCreationDataService} from '../../../services/young-creation-data.service';
import {NotifConnexionComponent} from '../../../utils/form-parts/form-items/notif-connexion/notif-connexion.component';
import {
  InscriptionAddSuppRegisterEventComponent
} from '../inscription-add-supp-register-event/inscription-add-supp-register-event.component';
import {YoungWebservice} from 'src/app/services/webservices/young.webservice';
import {DialogSelectCenterComponent} from 'src/app/utils/dialog/dialog-select-center/dialog-select-center.component';
import {dateInscription} from '../../../../environments/environment';
import {SchoolLevel} from '../../../model/user/school-level.model';
import {ParamWebservice} from '../../../services/webservices/param.webservice';
import {forkJoin, Subject} from 'rxjs';
import {Destroyed} from '../../../utils/directives/destroyed.directive';
import {debounceTime, take, takeUntil} from 'rxjs/operators';
import {YoungEditInformationDto} from '../../../model/dto/young-edit-information.dto';
import { PDisciplineScolarDifficulty } from 'src/app/model/param/p-discipline-scolar-difficulty';
import {PSchoolLevel} from '../../../model/param/p-school-level.model';
import { PScolarDifficulty } from 'src/app/model/param/p-scolar-difficulty';
import {SimpleYoungDto} from '../../../model/dto/simple-young.dto';
import {YoungAdditionnalInfoDto} from '../../../model/dto/young-additionnal-info.dto';
import {AT_SIGN, BACKSPACE, F12, META, NINE, NUMPAD_ZERO, SPACE, ZERO} from '@angular/cdk/keycodes';
import {getEnumKeysAsNumber, isNullOrUndefined} from '../../../utils/utils.static';
import {
  InscriptionInformationYoungComponent
} from '../inscription-information-young/inscription-information-young.component';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {
  GridEventMultiSelectComponent
} from '../../../utils/ag-grid/grid-event-multi-select/grid-event-multi-select.component';
import {MatExpansionModule} from '@angular/material/expansion';
import {AlertComponent} from '../../../utils/alert/alert/alert.component';
import {
  InputTextGoogleAutocompleteComponent
} from '../../../utils/form-parts/form-items/input-text-google-autocomplete/input-text-google-autocomplete.component';
import {SwitchComponent} from '../../../utils/form-parts/form-items/switch/switch.component';
import {SelectNumberComponent} from '../../../utils/form-parts/form-items/select-number/select-number.component';
import {SelectComponent} from '../../../utils/form-parts/form-items/select/select.component';
import {MatIconModule} from '@angular/material/icon';
import {MatDatepickerModule} from '@angular/material/datepicker';
import {MatInputModule} from '@angular/material/input';
import {MatFormFieldModule} from '@angular/material/form-field';
import {InputTextComponent} from '../../../utils/form-parts/form-items/input-text/input-text.component';
import { SelectMultipleComponent } from 'src/app/utils/form-parts/form-items/select-multiple/select-multiple.component';
import {NgClass, NgFor, NgIf} from '@angular/common';
import {SidebarComponent} from '../../sidebar/sidebar.component';
import {InscriptionService} from "./inscription.service";
import {LocalStorageService} from "../../../services/localstorage.service";

export class MyErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(control: UntypedFormControl | null, form: FormGroupDirective): boolean {
    const isSubmitted = form?.submitted;
    return !!(control && (control.dirty || control.touched || isSubmitted) && control.invalid && isSubmitted);
  }
}

@Component({
    selector: 'app-inscription',
    templateUrl: './inscription.component.html',
    styleUrls: ['./inscription.component.scss'],
    imports: [
      NgIf,
      NgFor,
        NgClass,
        MatFormFieldModule,
        MatCheckboxModule,
        MatInputModule,
        MatIconModule,
        MatExpansionModule,
        MatDatepickerModule,
        MatProgressSpinnerModule,
        FormsModule,
        ReactiveFormsModule,
        AlertComponent,
        GridEventMultiSelectComponent,
        InputTextComponent,
        InputTextGoogleAutocompleteComponent,
        InscriptionInformationYoungComponent,
        SelectMultipleComponent,
        SelectNumberComponent,
        SelectComponent,
        SidebarComponent,
        SwitchComponent
    ]
})
export class InscriptionComponent extends Destroyed implements OnInit {
  private readonly fb = inject(UntypedFormBuilder);
  private readonly formatService = inject(FormatService);
  private readonly searchWebservice = inject(SearchWebservice);
  private readonly treeDataService = inject(TreeDataService);
  private readonly dialog = inject(MatDialog);
  private readonly treeChoice = inject(TreeChoiceService);
  private readonly programWebservice = inject(ProgramWebservice);
  private readonly eventWebservice = inject(EventWebservice);
  private readonly stepperService = inject(StepperService);
  private readonly alertService = inject(AlertService);
  private readonly userWebservice = inject(UserWebservice);
  private readonly validationService = inject(ValidationService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly youngCreationDataService = inject(YoungCreationDataService);
  private readonly youngWS = inject(YoungWebservice);
  private readonly paramWebservice = inject(ParamWebservice);
  private readonly inscriptionService = inject(InscriptionService);
  private readonly localStorageService = inject(LocalStorageService);

  @ViewChild('container', {read: ViewContainerRef})
  container: ViewContainerRef;
  // form Group
  formGroupStepYoung: UntypedFormGroup;
  formGroupStepResp: UntypedFormGroup;
  formGroupStepPlace: UntypedFormGroup;
  // DTO YOUNG
  userToRegister: RegisterYoungDTO;
  user: User;
  young: Young;
  address: Address;
  schoolLevel: SchoolLevel;
  // DTO RESP
  responsables: RegisterResponsableDTO;
  // DTO EVENT
  eventToRegister: RegisterEventDTO;
  // DTO GLOBAL
  globalRegister: GlobalRegisterDTO;
  // List of selected places
  choicePlace: TreeItemFlatNode[] = [];
  // List of places names selected
  choicePlaceName: string[] = [];
  // Step of inscription
  step = 'young';
  stepNb = 1;
  nbStep: number;
  // Stepper type, progression with form submit be necessary
  isLinear = false;
  // True : two resp
  isDoubleResponsable = false;
  // Show of button add responsable / button switch between 2 responsable
  // False : one resp
  // State to resp 1 or 2
  isFirstResponsable = true;
  // Is loading to save
  isLoading = false;
  // Is valid double form resp
  isValidResponsable = false;
  // boolean to go next page
  nextPage = false;
  minBirthdayDate: Date;
  sexeEnum = SexeEnum;
  // phoneCode selection list
  phoneRegionCodeList: string[];
  phoneRegionCodeForSelect: FormSelectItem[] = [];
  phoneCodeResponsable1: number;
  phoneCodeResponsable2: number;
  phoneResponsable1: string;
  phoneResponsable2: string;
  mailResponsable1: string;
  mailResponsable2: string;
  // familyStatus selection list
  familySituationForSelect: FormSelectItem[] = [];
  // program selection list
  programList: Program[] = [];
  programForSelect: FormSelectItem[] = [];
  defaultProgram = 0;
  // place selection
  defaultPlace: TreeItemNode = null;
  additionalPlace: TreeItemNode[] = [];
  // eventTypeForSelect: FormSelectItem[] = [];
  defaultSelection: Event[] = []; // Contient ts les events (de ts les centres) pour aliment chaque nouvelle grid d'un nouveau centre

  // event selection list
  // eventTypeList: EventTypeDTO[];
  eventList: Event[] = []; // Contient a l'initialisation du champ event la liste des events pour la première place indiquée
  // attributs ajout event (f: younginfo)
  youngEvent: Young = null;
  isEventAdd = false;
  // validators section
  formYoungSubmitted = false;
  formRespSubmitted = false;
  formPlaceSubmitted = false;
  spinnerForm1 = false;
  sexValidator = false;
  sexRespValidator = false;
  birthdayMatcher = new MyErrorStateMatcher();
  isOlderThan17 = false;
  isPhoneStep1IfAdult = false; // Pour completer phone ecran 1
  isMailStep1IfAdult = false; // Pour completer phone ecran 1
  isPhoneSetStep2 = false; // Pour completer mail / phone ecran 2
  isIntegerSize = true;
  isIntegerWeight = true;
  hasValidatedAll = false;
  canChooseEvent = false;
  mailRegex = new RegExp('^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\\.[a-zA-Z0-9-.]+$');
  mailValidators = [
    Validators.pattern(this.mailRegex)];
  phoneRegex = new RegExp('(^0[6-7][0-9]{8}$)|(^[6-7][0-9]{8}$)');
  phoneValidators = [
    Validators.pattern(this.phoneRegex),
    Validators.maxLength(10),
    Validators.minLength(9)
  ];
  schoolLevelForSelect: FormSelectItem[] = [];
  chosenSchoolLevel: FormSelectItem;
  pSchoolLevelsArray: PSchoolLevel[] = [];
  pSchoolLevelsAllValues: PSchoolLevel[] = [];

  scolarDifficultyForSelect: FormSelectItem[] = [];
  disciplineScolarDifficultyForSelect: FormSelectItem[] = [];

  blockNext = false;
  isSameEmailAcrossResp = false;
  isSamePhoneAcrossResp = false;

  cancelMailExists = new Subject<void>();
  cancelPhoneExists = new Subject<void>();

  // information lors ajout d'une activité pour un jeune
  youngWithDataForModification = new YoungEditInformationDto();
  readonly alertStep1: string = 'alert-step-1';
  readonly alertStep1Phone: string = 'alert-step-1-phone';
  readonly alertStep1Email: string = 'alert-step-1-email';
  readonly alertStep2: string = 'alert-step-2';
  readonly alertStep2Phone: string = 'alert-step-2-phone';
  readonly alertStep2Email: string = 'alert-step-2-email';
  readonly alertStep3: string = 'alert-step-3';

  public blockNextYoung = computed(() => {
    return this.inscriptionService.alreadyExistsYoungPhone()
      || this.inscriptionService.alreadyExistsYoungEmail()
      || this.blockNext;
  });

  public blockNextStep = computed(() => {
    return this.inscriptionService.alreadyExistsEmail()
      || this.inscriptionService.alreadyExistsPhone()
      || this.inscriptionService.hasSameEmail()
      || this.inscriptionService.hasSamePhone()
      || this.isSamePhoneAcrossResp
      || this.isSameEmailAcrossResp;
  });
  public errorsCanGoNext = [];
  private readonly listOfComponentRef = [];
  private isAddYoung: boolean;
  private timeout = 2000;

  get hasSchoolDifficulties(): boolean {
    const noDifficultyOptionId = 2;
    return !!this.fStep1Young?.scolarDifficulty?.value?.includes(noDifficultyOptionId);
  }

  constructor() {
    super();

    effect(() => {
      this.errorsCanGoNext = [];
      if (this.inscriptionService.hasSameEmail()) {
        this.errorsCanGoNext.push('Les emails du jeune et des tuteurs doivent être différents');
      }
      if (this.inscriptionService.hasSamePhone()) {
        this.errorsCanGoNext.push('Les téléphones du jeune et des tuteurs doivent être différents');
      }

      if (this.inscriptionService.alreadyExistsEmail()) {
        this.errorsCanGoNext.push('Un email est déjà existant.');
      }
      if (this.inscriptionService.alreadyExistsPhone()) {
        this.errorsCanGoNext.push('Un téléphone est déjà existant.');
      }
    });
  }

  get isInvalidSchoolLevel() {
    return isNullOrUndefined(this.schoolLevel?.idPSchoolLevel)
      && this.fStep1Young.schoolLevel
      && (this.fStep1Young.schoolLevel.dirty
          || this.fStep1Young.schoolLevel.touched
          || this.formYoungSubmitted);
  }

  get isInvalidDisciplineDIfficulteScolaire() {
    return this.fStep1Young.scolarDifficulty.value?.includes(2)
      && (isNullOrUndefined(this.fStep1Young?.disciplineScolarDifficulty) || this.fStep1Young.disciplineScolarDifficulty.value.length === 0)
      && (this.fStep1Young.disciplineScolarDifficulty.dirty
          || this.fStep1Young.disciplineScolarDifficulty.touched
          || this.formYoungSubmitted);
  }

  /* Accesseurs form 1/2/3 */
  get fStep1Young() {
    return this.formGroupStepYoung.controls;
  }

  get fStep2Resp() {
    return this.formGroupStepResp.controls;
  }

  get fStep3Place() {
    return this.formGroupStepPlace.controls;
  }

  /*
   * ECRAN 1 : ENFANT
   */
  ngOnInit() {

    if (this.router.url === '/') {
      this.openModalConnexion();
    }
    this.treeDataService.reset();
    this.stepperService.setListStep(this.route.snapshot.data.stepList);
    this.isAddYoung = this.route.snapshot.data.isAddYoung !== null ? this.route.snapshot.data.isAddYoung : false;
    // Set default stepper position
    this.stepperService.initOrClear();
    this.step = this.stepperService.currentStep;
    this.stepNb = this.stepperService.currentStepNb;
    this.nbStep = this.stepperService.currentListStep.length;

    this.userToRegister = new RegisterYoungDTO();
    this.user = new User();
    this.young = new Young();
    this.address = new Address();
    this.schoolLevel = new SchoolLevel();

    // Recupere l'utilisateur pour la troisieme etape (si ajout event)
    if (this.route.snapshot.data.stepList.length === 1 && this.route.snapshot.data.stepList[0] === 'place') {
      this.isEventAdd = true;
      this.nbStep = 2;
      this.timeout = 1000;

      const id = Number.parseInt(this.localStorageService.getItem("youngId") ?? '0', 10);

      this.initformGroupStepPlace();
      this.youngWS.getYoung(id).pipe(takeUntil(this.destroyed)).subscribe((value: SimpleYoungDto) => {
        this.userToRegister.youngRegistered = value.young;
        this.userToRegister.userRegistered = value.user;
        this.eventToRegister.userRegistered = this.userToRegister.userRegistered;
        this.initPlaceSelector();
      });
    }

    this.initFormGroupStep1();

    // Défini la dernière année scolaire
    this.getScolarDate();

    // Défini l'age minimum d'un young
    this.getEndDate();

    this.initPhoneCodeSelector(1);

    this.handleFormGroupChanges();

    if (this.isAddYoung) {
      this.prefillYoungAddressFromConnectedUser();
    }

    this.initSchoolLevel();

    this.initScolarDifficulty();
    this.initDisciplineScolarDifficulty();
  }

  checkPhoneOrMailStep1IfAdult() { // Si majeur, vérifie que le phone est saisi
    if (this.isOlderThan17) {
      this.isPhoneStep1IfAdult = (this.formGroupStepYoung.controls.phone.value && this.formGroupStepYoung.controls.phone.value !== '');
      this.isMailStep1IfAdult = (this.formGroupStepYoung.controls.mail.value && this.formGroupStepYoung.controls.mail.value !== '');
    }
  }

  initPhoneCodeSelector(form: number) {
    this.phoneRegionCodeList = [];
    this.phoneRegionCodeForSelect = [];

    const PhoneRegionCodeEnumLength = Object.keys(PhoneRegionCodeEnum).length / 2;

    for (let i = 0; i < PhoneRegionCodeEnumLength; i++) {
      this.phoneRegionCodeList[i] = PhoneRegionCodeEnum[i];
    }

    for (const key of this.phoneRegionCodeList.keys()) { // Création du tableau pour le select, ici <id, name>
      this.phoneRegionCodeForSelect.push({id: Number(key), name: this.phoneRegionCodeList[key]});
    }

    if (form === 1) {
      this.formGroupStepYoung.controls.phoneCode.setValue(0);
    }
    if (form === 2) {
      this.formGroupStepResp.controls.phoneCode.setValue(0);
    }
  }

  onMaleSelect(event: MatCheckboxChange) { // Switch les sexes si l'utilisateur selectionne l'un puis l'autre
    if (event.checked === true) {
      this.formGroupStepYoung.controls.sexMale.setValue(true);
      this.formGroupStepYoung.controls.sexFemale.setValue(false);
      this.sexValidator = true;
      this.user.sex = 0;
    } else {
      this.formGroupStepYoung.controls.sexMale.setValue(false);
      if (this.formGroupStepYoung.controls.sexFemale.value === false) {
        this.sexValidator = false;
      }
    }
  }

  /*
   * ECRAN 2 : RESPONSABLE
   */

  onFemaleSelect(event: MatCheckboxChange) { // Switch les sexes si l'utilisateur selectionne l'un puis l'autre
    if (event.checked === true) {
      this.formGroupStepYoung.controls.sexFemale.setValue(true);
      this.formGroupStepYoung.controls.sexMale.setValue(false);
      this.sexValidator = true;
      this.user.sex = 1;
    } else {
      this.formGroupStepYoung.controls.sexFemale.setValue(false);

      if (this.formGroupStepYoung.controls.sexMale.value === false) {
        this.sexValidator = false;
      }
    }
  }

  openModalConnexion() {
    this.dialog.open(NotifConnexionComponent, {
      panelClass: 'dialog'
    });
  }

  goNextStep() {
    const nextStep = this.stepperService.currentListStep[this.stepNb + 1];
    switch (nextStep) {
      case 'resp':
        this.stepResp();
        break;
      case 'place':
        if (this.step === 'young') {
          this.stepYoung();
        } else {
          this.stepPlace();
        }
        break;
      default:
        break;
    }
    if (this.nextPage) {
      this.stepperService.nextPage();
      this.step = this.stepperService.currentStep;
      this.stepNb = this.stepperService.currentStepNb;
    }
    this.nextPage = false;
  }

  stepResp() {
    this.formYoungSubmitted = true;
    if (!this.formGroupStepYoung.invalid && this.isPhoneStep1IfAdult && this.isMailStep1IfAdult && !this.isInvalidDisciplineDIfficulteScolaire) {
      this.userToRegister.userRegistered = this.user;
      this.userToRegister.youngRegistered = this.young;
      this.userToRegister.schoolLevelYoung = this.schoolLevel;
      this.userToRegister.addressYoung = this.address;
      this.userToRegister.listOfYoungPDifficulteScolaireIds = this.fStep1Young.scolarDifficulty.value;
      this.userToRegister.listOfYoungPDisciplineDifficulteScolaireIds = this.fStep1Young.disciplineScolarDifficulty.value;

      this.youngCreationDataService.changeCurrentUser(this.user);

      if (!this.formGroupStepResp) {
        this.initformGroupStepResp();
      }
      this.nextPage = true;
    }
  }

  initformGroupStepResp() {
    // Step 2
    this.formGroupStepResp = this.fb.group({
      lastName: [null, Validators.required],
      firstName: [null, Validators.required],
      sex: [null, Validators.required],
      familySituation: [null, [
        Validators.required
      ]],
      job: null,
      phoneCode: [null, Validators.required],
      phone: [null, [
        Validators.required,
        ...this.phoneValidators
      ]],
      mail: [null, [
        Validators.required,
        ...this.mailValidators
      ]],
      address1: [null, Validators.required],
      zipCode: [null, Validators.required],
      city: [null, Validators.required]
    });

    this.initFamilySituationSelector();
    this.initPhoneCodeSelector(2);

    this.initResp();

    // Recupere l'adresse du jeune et l'attribue au userResponsable + form
    this.formGroupStepResp.controls.address1.setValue(this.formGroupStepYoung.controls.address1.value);
    this.formGroupStepResp.controls.zipCode.setValue(this.formGroupStepYoung.controls.zipCode.value);
    this.formGroupStepResp.controls.city.setValue(this.formGroupStepYoung.controls.city.value);

    this.responsables.userResponsable.address.address1 = this.formGroupStepYoung.controls.address1.value;
    this.responsables.userResponsable.address.zipCode = this.formGroupStepYoung.controls.zipCode.value;
    this.responsables.userResponsable.address.city = this.formGroupStepYoung.controls.city.value;

    // OnChange selon le responsable selectionne
    this.formGroupStepResp.controls.lastName.valueChanges.pipe(takeUntil(this.destroyed)).subscribe(value => {
      if (this.isFirstResponsable && this.responsables && this.responsables.userResponsable) {
        this.responsables.userResponsable.lastName = value;
      } else if (this.responsables.userSecondResponsable) {
        this.responsables.userSecondResponsable.lastName = value;
      }
    });
    this.formGroupStepResp.controls.firstName.valueChanges.pipe(takeUntil(this.destroyed)).subscribe(value => {
      if (this.isFirstResponsable && this.responsables.userResponsable) {
        this.responsables.userResponsable.firstName = value;
      } else if (this.responsables.userSecondResponsable) {
        this.responsables.userSecondResponsable.firstName = value;
      }
    });
    this.formGroupStepResp.controls.sex.valueChanges.pipe(takeUntil(this.destroyed)).subscribe(value => {
        this.sexRespValidator = true;
        if (this.isFirstResponsable && this.responsables.userResponsable) {
          this.responsables.userResponsable.sex = value;
        } else if (this.responsables.userSecondResponsable) {
          this.responsables.userSecondResponsable.sex = value;
        }
      }
    );
    this.formGroupStepResp.controls.familySituation.valueChanges.pipe(takeUntil(this.destroyed)).subscribe(value => {
      if (this.isFirstResponsable && this.responsables.userFamily) {
        this.responsables.userFamily.familySituation = value;
      } else if (this.responsables.userFamilySecondResponsable) {
        this.responsables.userFamilySecondResponsable.familySituation = value;
      }
    });
    this.formGroupStepResp.controls.job.valueChanges.pipe(takeUntil(this.destroyed)).subscribe(value => {
      if (this.isFirstResponsable && this.responsables.userFamily) {
        this.responsables.userFamily.job = value;
      } else if (this.responsables.userFamilySecondResponsable) {
        this.responsables.userFamilySecondResponsable.job = value;
      }
    });
    this.formGroupStepResp.controls.mail.valueChanges.pipe(debounceTime(200), takeUntil(this.destroyed)).subscribe(value => {
      if (this.isFirstResponsable && this.responsables.userResponsable) {
        this.responsables.userResponsable.email = value;
        this.validateAndSetMailResp(value);
      } else if (this.responsables.userSecondResponsable) {
        this.responsables.userSecondResponsable.email = value;
        this.validateAndSetMailResp(value, true);
      }
    });
    this.formGroupStepResp.controls.phoneCode.valueChanges.pipe(debounceTime(200), takeUntil(this.destroyed)).subscribe(value => {
      if (this.isFirstResponsable) {
        this.phoneCodeResponsable1 = value; // attention, pas sous la forme '+33' (index du select)
      } else {
        this.phoneCodeResponsable2 = value;
      }
    });
    this.formGroupStepResp.controls.phone.valueChanges.pipe(debounceTime(200), takeUntil(this.destroyed)).subscribe((value: string) => {
      if (value !== null) {
        if (this.isFirstResponsable) {
          this.validateAndSetPhoneNumberResp(value);
        } else {
          this.validateAndSetPhoneNumberResp(value, true);
        }
      } else {
        this.isPhoneSetStep2 = false;
        this.alertService.clear(this.alertStep2Phone);
        this.inscriptionService.resetRespPhone(this.isFirstResponsable);
      }
      this.isPhoneStep2();

    });
    this.formGroupStepResp.controls.address1.valueChanges.pipe(takeUntil(this.destroyed), debounceTime(500)).subscribe(value => {
      if (this.isFirstResponsable && this.responsables.userResponsable) {
        this.responsables.userResponsable.address.address1 = value;
      } else if (this.responsables.userSecondResponsable) {
        this.responsables.userSecondResponsable.address.address1 = value;
      }
    });
    this.formGroupStepResp.controls.zipCode.valueChanges.pipe(takeUntil(this.destroyed), debounceTime(500)).subscribe(value => {
      if (this.isFirstResponsable && this.responsables.userResponsable) {
        this.responsables.userResponsable.address.zipCode = value;
      } else if (this.responsables.userSecondResponsable) {
        this.responsables.userSecondResponsable.address.zipCode = value;
      }
    });
    this.formGroupStepResp.controls.city.valueChanges.pipe(takeUntil(this.destroyed), debounceTime(500)).subscribe(value => {
      if (this.isFirstResponsable && this.responsables.userResponsable) {
        this.responsables.userResponsable.address.city = value;
      } else if (this.responsables.userSecondResponsable) {
        this.responsables.userSecondResponsable.address.city = value;
      }
    });

    this.formGroupStepResp.controls.phoneCode.setValue(0);
  }

  initResp() {
    this.responsables = new RegisterResponsableDTO();
    this.responsables.userFamily = new RelationUser();
    this.responsables.userFamily.familySituation = FamilySituationEnum.COUPLE;
    this.responsables.userResponsable = new User();
    this.phoneCodeResponsable1 = 0;
    this.responsables.userResponsable.sex = SexeEnum.MAN; // default
    this.responsables.userResponsable.address = new Address();
    this.responsables.isSecondResponsable = false;
  }

  isPhoneStep2() { // Si le tel est renseigné OK
    this.isPhoneSetStep2 = (this.formGroupStepResp.controls.phone.value && this.formGroupStepResp.controls.phone.value !== '');
  }

  initFamilySituationSelector() {
    this.familySituationForSelect = [];

    getEnumKeysAsNumber(FamilySituationEnum).forEach((key: number) => {
      this.familySituationForSelect.push({
        id: key,
        name: this.formatService.formatFamilySituationEnum(key)
      });
    });
  }

  addResponsable() {
    this.isDoubleResponsable = true;
    this.isFirstResponsable = true;

    this.responsables.isSecondResponsable = true;
    this.responsables.userFamilySecondResponsable = new RelationUser();
    this.responsables.userFamilySecondResponsable.familySituation = FamilySituationEnum.COUPLE;
    this.responsables.userSecondResponsable = new User();
    this.responsables.userSecondResponsable.sex = this.sexeEnum.MAN; // default
    this.responsables.userSecondResponsable.address = new Address();
    // Recupere l'adresse du jeune et l'attribue au userSecondResponsable + form
    this.responsables.userSecondResponsable.address.address1 = this.formGroupStepYoung.controls.address1.value;
    this.responsables.userSecondResponsable.address.zipCode = this.formGroupStepYoung.controls.zipCode.value;
    this.responsables.userSecondResponsable.address.city = this.formGroupStepYoung.controls.city.value;
    this.formGroupStepResp.controls.address1.setValue(this.formGroupStepYoung.controls.address1.value);
    this.formGroupStepResp.controls.zipCode.setValue(this.formGroupStepYoung.controls.zipCode.value);
    this.formGroupStepResp.controls.city.setValue(this.formGroupStepYoung.controls.city.value);

    this.phoneCodeResponsable2 = 0;
  }

  cancelAdd() {
    if (!this.isFirstResponsable) {
      this.switchResponsable();
    } else {
      this.isFirstResponsable = true;
    }

    this.isDoubleResponsable = false;
    this.responsables.isSecondResponsable = false;
    this.responsables.userFamilySecondResponsable = null;
    this.responsables.userSecondResponsable = null;
    this.phoneResponsable2 = null;
  }

  switchResponsable() {
    this.isFirstResponsable = !this.isFirstResponsable;


    // Charge le formulaire selon le responsable attendu
    if (this.isFirstResponsable) {
      this.setFormResponsable(this.responsables.userResponsable, this.responsables.userFamily, this.phoneCodeResponsable1, this.phoneResponsable1);
    } else {
      this.setFormResponsable(this.responsables.userSecondResponsable, this.responsables.userFamilySecondResponsable, this.phoneCodeResponsable2, this.phoneResponsable2);
    }
  }

  stepPlaceWithoutResp(skipStepResp = false) {
    if (!this.formGroupStepPlace) {
      this.initformGroupStepPlace();
      this.initPlaceSelector();
    } else {
      this.validationService.setValidationState(true);
      this.initPlaceSelector(); // Recharge les centres si modif du CP
      this.initEventSelector(); // Recharge les eventType si modif du sexe de l'enfant
      // Recharge le centre si non-selectionne dans l'input text
      if (this.eventToRegister && this.defaultPlace) {
        this.formGroupStepPlace.controls.nameCenter.setValue(this.defaultPlace.name);
      }
    }
    this.responsables = undefined;
    this.nextPage = true;
    if (skipStepResp) {
      this.stepperService.nextPage();
      this.step = this.stepperService.currentStep;
      this.stepNb = this.stepperService.currentStepNb;
    }
  }

  /*
   * ECRAN 3 : EVENT
   */

  /*
  validatorAsInteger: ValidatorFn = (control: FormGroup): ValidationErrors | null => {
    if (control != null && control.value != null) {
      const valid = Number.isInteger(control.value) && control.value > 0;
      return valid ? null : {badFormat: { valid: false, value: control.value }};
    }
  }*/

  stepPlace() {
    this.isValidResponsable = true;
    this.formRespSubmitted = true;
    if (!this.formGroupStepResp.invalid && this.isPhoneSetStep2) {
      // Seconde verification, sur le second responsable si existant
      this.verificationResponsables();

      if (this.isValidResponsable) { // Si le / les deux forms ont bien été renseignes
        this.initializeAndSetPhoneForResponsibles();
        this.nextPage = true;
      }
    }
  }

  initformGroupStepPlace() {
    // Step 3
    this.formGroupStepPlace = this.fb.group({
      size: [null],
      weight: [null],
      nameCenter: [null, Validators.required],
      program: [null, Validators.required],
      eventType: [{value: null, disabled: true}]
    });

    this.programWebservice.getPrograms().pipe(takeUntil(this.destroyed)).subscribe(
      (value: Program[]) => {
        this.programList = [value.find(prog => prog.id === 1)];

        this.initProgramSelector();
      }
    );

    this.eventToRegister = new RegisterEventDTO();
    this.eventToRegister.ListOfPlaceEvent = [];
    this.eventToRegister.program = new Program();
    this.eventToRegister.userRegistered = this.userToRegister.userRegistered;

    this.formGroupStepPlace.controls.size.valueChanges.pipe(takeUntil(this.destroyed)).subscribe(value => {
      if (value > 0) {
        this.isIntegerSize = true;
        this.userToRegister.youngRegistered.height = value;
      } else {
        this.isIntegerSize = false;
      }
    });

    this.formGroupStepPlace.controls.weight.valueChanges.pipe(takeUntil(this.destroyed)).subscribe(value => {
      if (value > 0) {
        this.isIntegerWeight = true;
        this.userToRegister.youngRegistered.weight = value;
      } else {
        this.isIntegerWeight = false;
      }
    });

    this.formGroupStepPlace.controls.program.valueChanges.pipe(takeUntil(this.destroyed)).subscribe((value: number) => {
      const tempProg = this.programForSelect.find(prog => prog.id === value);
      this.eventToRegister.program.id = tempProg.id;
      this.eventToRegister.program.programType = tempProg.name;
    });

    this.formGroupStepPlace.controls.nameCenter.valueChanges.pipe(takeUntil(this.destroyed)).subscribe(() => {
      this.youngCreationDataService.resetRegisterEvent();
    });

    this.validationService.setValidationState(true);
  }

  openModalPlace() {
    const dialogRef = this.dialog.open(DialogSelectCenterComponent, {
      panelClass: 'dialog'
    });

    dialogRef.afterClosed().pipe(takeUntil(this.destroyed)).subscribe((value: TreeItemNode) => {
      if (value?.name) {
        this.treeChoice.setPlace('placeListPlace', [value.name]);

        // Ajoute le centre aux exclus
        if (value.id) {
          if (this.defaultPlace) {
            this.treeDataService.removeExcludePlace(this.defaultPlace.id);
            this.defaultPlace = value;
            this.treeDataService.addExcludePlace(value);
          } else {
            this.treeDataService.addExcludePlace(value);
            this.defaultPlace = value;
          }
          // Recharge la liste pour le bug suppression de centre (disparition de centre)
          this.initPlaceSelector();
        }

        this.formGroupStepPlace.controls.nameCenter.setValue(value.name);

        // Recupere les types en fonction de la dispo des centres
        this.initEventSelector();
        this.formGroupStepPlace.controls.eventType.enable();
        this.canChooseEvent = true;
      } else if (!this.treeChoice.hasPlace('placeListPlace')) {
        this.formGroupStepPlace.controls.eventType.disable();
        this.canChooseEvent = false;
      }
    });
  }

  initPlaceSelector() {
    this.searchWebservice.getPlacesTree(
      (this.isEventAdd) ? +this.userToRegister.userRegistered.address.zipCode.slice(0, 2)
        : +this.formGroupStepYoung.controls.zipCode.value.slice(0, 2))
      .pipe(takeUntil(this.destroyed)).subscribe((data: TreeItemNode[]) => {
        this.treeDataService.changeAllPlace(data);
      }
    );

    this.treeDataService.choixPlacesInscriptionMessager.pipe(takeUntil(this.destroyed)).subscribe(data => {
      this.choicePlace = data;
      this.choicePlaceName = [];
      // set place in input for the user
      this.choicePlace.forEach(place => {
        this.choicePlaceName.push(place.name);
      });
      this.treeChoice.setPlace('placeListPlace', this.choicePlaceName);
      if (this.formGroupStepPlace.controls.nameCenter.value) {
        this.formGroupStepPlace.controls.nameCenter.setValue(this.formatService.formatPlaceTreeInputDisplay(this.choicePlace));
      }
    });
  }

  initProgramSelector() {
    this.programForSelect = [{}];
    this.programList.forEach(value => {
      this.programForSelect.push({id: value.id, name: value.programType}); // Création du tableau pour le select, ici <Programme, name>
    });

    this.formGroupStepPlace.controls.program.setValue(1);
  }

  initEventSelector() {
    // Recupere la liste globale pour les ajouts de centre
    if (this.defaultPlace?.id) {
      this.eventWebservice.getEventTypes(
        this.userToRegister.userRegistered.birthday,
        this.userToRegister.userRegistered.sex,
        this.defaultPlace.id,
        this.userToRegister.userRegistered.id ? this.userToRegister.userRegistered.id : 0)
        .pipe(takeUntil(this.destroyed)).subscribe(value => {
        this.defaultSelection = value;
        this.eventList = value;
        this.youngCreationDataService.changeCurrentListOfAllEvent(value);
        if (!value) {
          this.alertService.error(
            'Aucun événement n\'est existant sur cette tranche d\'âge et ce centre, veuillez changer le centre', this.alertStep3);
        }
      });
    }
  }

  onPlaceRemove(index: number) {
    const component = this.listOfComponentRef.find(
      item => item.instance instanceof InscriptionAddSuppRegisterEventComponent
        && item.instance.index === index);
    const componentIndex = this.listOfComponentRef.indexOf(component);

    if (componentIndex !== -1) {
      // Remove component from both view and array
      this.container.remove(this.container.indexOf(component));
      this.listOfComponentRef.splice(componentIndex, 1);
    }
  }

  omitSpecialChar(event) {
    const k = event.charCode;  //         k = event.keyCode;  (Both can be used)
    return ((k > AT_SIGN && k < META) || (k > NUMPAD_ZERO && k < F12) || k === BACKSPACE || k === SPACE || (k >= ZERO && k <= NINE));
  }

  onSelectionChange(event) {
    this.youngCreationDataService.setRegisterEvent(event, this.defaultPlace.id);
  }

  onYoungAddressChange(event: string) {
    this.address.address1 = event;
  }

  onRespAddressChange(event: string) {
    if (this.isFirstResponsable) {
      this.responsables.userResponsable.address.address1 = event;
    } else {
      this.responsables.userSecondResponsable.address.address1 = event;
    }
  }

  onYoungZipCodeChange(event: string) {
    this.formGroupStepYoung.get('zipCode').setValue(event);
    this.address.zipCode = event;
  }

  onYoungCityChange(event: string) {
    this.formGroupStepYoung.get('city').setValue(event);
    this.address.city = event;
  }

  onRespZipCodeChange(event: string) {
    this.formGroupStepResp.get('zipCode').setValue(event);
    this.address.zipCode = event;
  }

  onRespCityChange(event: string) {
    this.formGroupStepResp.get('city').setValue(event);
    this.address.city = event;
  }

  validForm() {
    if (!this.formPlaceSubmitted && !this.hasValidatedAll) {
      this.formPlaceSubmitted = true;
      this.hasValidatedAll = false;
      this.isLoading = true;

      if (!this.formGroupStepPlace.invalid) {
        this.validationService.setFormState(true);
        this.globalRegister = new GlobalRegisterDTO();
        this.globalRegister.youngRegister = this.userToRegister;
        this.globalRegister.responsableYoung = this.responsables;
        this.globalRegister.eventToRegister = this.eventToRegister;

        // Recupere les informations sur les evenements ou le jeune s'est inscrit

        const listOfSelectedPlaceAndEvent = this.youngCreationDataService.getRegisterEvent();

        const placeEvent = null;
        this.registerPlaceEvent(listOfSelectedPlaceAndEvent, placeEvent);

        // send global informations of register
        if (this.hasValidatedAll) {
          this.save();
        }
      } else {
        this.isLoading = false;
        this.formPlaceSubmitted = false;
        this.hasValidatedAll = false;
        this.validationService.setFormState(false);
      }
    }
  }

  back() {
    this.isLoading = false;
    this.stepperService.previousPage();
    this.step = this.stepperService.currentStep;
    this.stepNb = this.stepperService.currentStepNb;
    this.validationService.setValidationState(false);

    if (this.step === 'resp' && !this.responsables) {
      this.initResp();
    }
  }

  // date du jour - 6 ans
  getEndDate() {
    const today = new Date();
    this.minBirthdayDate = new Date(today.getFullYear() - 6, today.getMonth(), today.getDate());
  }

  getScolarDate() {
    const date = new Date();
    if (date < dateInscription.limitDate) {
      this.schoolLevel.dateStart = new Date(dateInscription.limitDate.getFullYear() - 1, 8, 1);
      this.schoolLevel.dateEnd = new Date(dateInscription.limitDate.getFullYear(), 7, 31);
      this.fStep1Young.schoolLevelYear.setValue(this.schoolLevel.dateStart);
    } else {
      this.schoolLevel.dateStart = new Date(dateInscription.limitDate.getFullYear(), 8, 1);
      this.schoolLevel.dateEnd = new Date(dateInscription.limitDate.getFullYear() + 1, 7, 31);
      this.fStep1Young.schoolLevelYear.setValue(this.schoolLevel.dateStart);
    }
  }

  formatScolarDate() {
    return this.schoolLevel.dateStart.getFullYear() + '/' + (this.schoolLevel.dateStart.getFullYear() + 1);
  }

  onChangeLevel($event) {
    this.schoolLevel.idPSchoolLevel = $event;
    this.fStep1Young.schoolLevel.setValue($event);
  }

  isBirthdayFalse() {
    if (this.fStep1Young.birthday.value) {
      return this.formatService.formatAge(this.fStep1Young.birthday.value) < 6;
    }
    return '';
  }

  replaceTreeWithAllValues(pSchoolLevel: PSchoolLevel) {
    if (pSchoolLevel.listOfPSchoolLevel) {
      this.pSchoolLevelsAllValues.push(pSchoolLevel);
      pSchoolLevel.listOfPSchoolLevel.forEach(item => this.replaceTreeWithAllValues(item));
    } else {
      this.pSchoolLevelsAllValues.push(pSchoolLevel);
    }
  }

  initDataInformationYoung() {
    this.stepNb = 2;
    this.step = 'modificationYoung';

    let valueInfoFromYoungObs: YoungAdditionnalInfoDto;
    const yearActual = new Date();
    let year;

    if (yearActual > dateInscription.limitDate
      && (yearActual.getFullYear() - dateInscription.limitDate.getFullYear() < 1)) {
      year = yearActual.getFullYear();
    } else {
      year = yearActual.getFullYear() - 1;
    }

    forkJoin({
      allSchoolLevelsObs: this.paramWebservice.getAllSchoolLevels(),
      getInfoFromYoungObs: this.youngWS.getInfosFromYoung(this.userToRegister.youngRegistered.id)
    }).subscribe(({allSchoolLevelsObs, getInfoFromYoungObs}) => {
      this.pSchoolLevelsArray = allSchoolLevelsObs;
      this.pSchoolLevelsArray.forEach(item => this.replaceTreeWithAllValues(item));

      valueInfoFromYoungObs = getInfoFromYoungObs;

      this.youngWithDataForModification.schoolLevel = new SchoolLevel();

      if (valueInfoFromYoungObs?.listOfSchoolLevel.length > 0
        && valueInfoFromYoungObs.listOfSchoolLevel.some(item => new Date(item.dateStart).getFullYear() === year)) {
        this.youngWithDataForModification.schoolLevel = valueInfoFromYoungObs.listOfSchoolLevel.filter(item => new Date(item.dateStart).getFullYear() === year)[0];
      }
      this.youngWithDataForModification.id = this.userToRegister.youngRegistered.id;
      this.youngWithDataForModification.firstName = this.userToRegister.userRegistered.firstName;
      this.youngWithDataForModification.lastName = this.userToRegister.userRegistered.lastName;
      this.youngWithDataForModification.medicalObservation = this.userToRegister.youngRegistered.medicalObservation;
      if (this.youngWithDataForModification.schoolLevel[0]) {
        this.youngWithDataForModification.schoolLevel = this.youngWithDataForModification.schoolLevel[0];
      }

      if (this.userToRegister.youngRegistered.handicap && this.userToRegister.youngRegistered.handicap.toLowerCase() === 'true') {
        this.youngWithDataForModification.handicap = 'true';
      } else {
        this.youngWithDataForModification.handicap = 'false';
      }
    });
  }

  parentNextStepModificationYoung() {
    return this.router.navigate(['/info/' + this.userToRegister.userRegistered.id]);
  }

  isBirthdayErrorState(control: UntypedFormControl | null): boolean {
    return !!(control && (control.dirty || control.touched) && control.invalid);
  }

  tronqPhoneNumber(value) {
    let telTronq = '';
    if (value.length > 8 && value.length <= 10) {
      telTronq = value.length === 10 ? value.slice(1) : value;
      this.user.phone = `${this.phoneRegionCodeList[this.formGroupStepYoung.controls.phoneCode.value]}${telTronq}`;
      this.inscriptionService.setYoungPhone(this.user.phone);
    }
  }

  handlePhoneExists() {
    this.cancelPhoneExists.next();
    this.userWebservice.postCheckPhoneExist(this.user.phone)
      .pipe(debounceTime(200), takeUntil(this.cancelPhoneExists), this.untilDestroyed())
      .subscribe({
        next: res => {
          if (res === true) {
            this.inscriptionService.setAlreadyExistsYoungPhone(true);
            this.alertService.clear(this.alertStep1Phone);
            this.alertService.error('Un utilisateur ayant le même numéro de téléphone existe déjà !', this.alertStep1Phone, this.user.phone);
            this.blockNext = true;
            this.formGroupStepYoung.get('phone').setErrors({'incorrect': true});
          } else {
            this.blockNext = false;
            this.alertService.clear(this.alertStep1Phone);
            this.isPhoneStep1IfAdult = true;
            this.inscriptionService.setAlreadyExistsYoungPhone(false);
          }
        }
      });
  }

  testPhoneNumber(value: string) {
    return value?.length > 8 && value?.length <= 10 && this.phoneRegex.test(value);
  }

  getValuePhoneResp(value: string, isResp2: boolean) {
    if (isResp2) {
      this.phoneResponsable2 = value;
    } else {
      this.phoneResponsable1 = value;
    }
  }

  handlePhoneExistsErrors(telBdd, isResp2) {
    this.cancelPhoneExists.next();
    this.userWebservice.postCheckPhoneExist(telBdd)
      .pipe(takeUntil(this.cancelPhoneExists), takeUntil(this.destroyed))
      .subscribe((res: boolean) => {

        this.isPhoneSetStep2 = !res;
        this.alertService.clear(this.alertStep2Phone);
        if (isResp2) {
          this.inscriptionService.setAlreadyExistsResp2Phone(res);
        } else {
          this.inscriptionService.setAlreadyExistsResp1Phone(res);
        }
        if (res) {
          this.alertService.error('Un utilisateur ayant le même numéro de ' +
            'téléphone que ce responsable existe déjà !', this.alertStep2Phone, telBdd);
        }
      });
  }

  handleResPhonesNumber(telBdd, isResp2) {
    this.alertService.clear(this.alertStep2);
    if (isResp2) {
      if (this.phoneResponsable1
        && telBdd === `${this.phoneRegionCodeList[this.formGroupStepResp.controls.phoneCode.value]}${this.phoneResponsable1.slice(1)}`) {
        this.alertService.error('Les responsables légaux doivent avoir des numéros de téléphone différents.', this.alertStep2);
        this.isSamePhoneAcrossResp = true;
      } else {
        this.isSamePhoneAcrossResp = false;
      }
    } else if (this.phoneResponsable2
      && telBdd === `${this.phoneRegionCodeList[this.formGroupStepResp.controls.phoneCode.value]}${this.phoneResponsable2.slice(1)}`) {
      this.alertService.error('Les responsables légaux doivent avoir des numéros de téléphone différents.', this.alertStep2);
      this.isSamePhoneAcrossResp = true;
    } else {
      this.isSamePhoneAcrossResp = false;
    }

  }

  getValueMailResp(isResp2, value) {
    if (isResp2) {
      this.mailResponsable2 = value;
      this.inscriptionService.setResp2Email(value.trim());
    } else {
      this.mailResponsable1 = value;
      this.inscriptionService.setResp1Email(value.trim());
    }
  }

  handleMailExistsErrors(value, isResp2: boolean) {
    if (this.mailRegex.test(value)) {
      this.cancelMailExists.next();
      this.userWebservice
        .postCheckEmailExist(value)
        .pipe(takeUntil(this.cancelMailExists), this.untilDestroyed())
        .subscribe((bool: boolean) => {
          this.alertService.clear(this.alertStep2Email);
          if (isResp2) {
            this.inscriptionService.setAlreadyExistsResp2Email(bool);
          } else {
            this.inscriptionService.setAlreadyExistsResp1Email(bool);
          }

          if (bool) {
            this.alertService.error('Un utilisateur ayant le même email existe déjà !', this.alertStep2Email, value);
          }
        });
    }
  }

  handleTwoRespErrors() {
    if (this.mailResponsable1 === this.mailResponsable2) {
      this.alertService.clear(this.alertStep2);
      this.alertService.error(`Les responsables légaux doivent avoir des adresses mail différentes.`,
        this.alertStep2);
      this.isSameEmailAcrossResp = true;
    } else {
      this.alertService.clear(this.alertStep2);
      this.isSameEmailAcrossResp = false;
    }
  }

  refreshCurrentUser() {
    if (!this.userWebservice.currentUser) {
      this.userWebservice.refreshCurrentUser().pipe(take(1))
        .subscribe({
          next: (data: User) => {
            this.userWebservice.currentUser = data;
            this.userWebservice.currentUserModule = data.listOfModule;
          },
          error: () => {
            this.userWebservice.currentUser = new User();
            this.userWebservice.currentUser.id = -1;
          }
        });
    }
  }

  private prefillYoungAddressFromConnectedUser() {
    this.userWebservice.refreshCurrentUser().pipe(take(1), takeUntil(this.destroyed))
      .subscribe((user: User) => {
        this.userWebservice.currentUser = user;
        const address = user?.address;
        if (!address) {
          return;
        }
        this.formGroupStepYoung.patchValue({
          address1: address.address1,
          zipCode: address.zipCode,
          city: address.city
        });
      });
  }

  handleEmailExistsAlreadyError(alreadyExists: boolean) {
    this.inscriptionService.setAlreadyExistsYoungEmail(alreadyExists);
    this.blockNext = alreadyExists;
    this.alertService.clear(this.alertStep1Email);
    if (alreadyExists) {
      this.formGroupStepYoung.get('mail').setErrors({'incorrect': true});
      this.alertService.error('Un utilisateur ayant le même email existe déjà !', this.alertStep1Email, this.user.email);
    } else {
      this.isMailStep1IfAdult = true;
    }
  }

  private initializeAndSetPhoneForResponsibles() {
    if (!this.formGroupStepPlace) {
      // Si le form n'a pas encore été vu
      // Attribue le numero avec son préfixe
      if (this.phoneResponsable1) {
        const phone = this.phoneResponsable1.startsWith('0') ? this.phoneResponsable1.slice(1) : this.phoneResponsable1;
        this.responsables.userResponsable.phone =
          `${this.phoneRegionCodeList[this.phoneCodeResponsable1]}${phone}`;
      }
      if (this.responsables.userSecondResponsable && this.phoneResponsable2) {
        const phone = this.phoneResponsable2.startsWith('0') ? this.phoneResponsable2.slice(1) : this.phoneResponsable2;
        this.responsables.userSecondResponsable.phone =
          `${this.phoneRegionCodeList[this.phoneCodeResponsable2]}${phone}`;
      }
      this.initformGroupStepPlace();
      this.initPlaceSelector();
    } else {
      this.validationService.setValidationState(true);
      this.initPlaceSelector(); // Recharge les centres si modif du CP
      this.initEventSelector(); // Recharge les eventType si modif du sexe de l'enfant
      // Recharge le centre si non-selectionne dans l'input text
      if (this.eventToRegister && this.defaultPlace) {
        this.formGroupStepPlace.controls.nameCenter.setValue(this.defaultPlace.name);
      }
    }
  }

  private verificationResponsables() {
    if (this.responsables.isSecondResponsable) {
      if (this.isFirstResponsable
        && this.checkInfoResponsable2()) {
        this.alertService.clear(this.alertStep2);
        this.alertService.error('Des informations sur le second responsable sont manquantes !', this.alertStep2);
        this.isValidResponsable = false;
      } else if (!this.isFirstResponsable && this.checkInfoResponsable1()) {
        this.alertService.clear(this.alertStep2);
        this.alertService.error('Des informations sur le premier responsable sont manquantes !', this.alertStep2);
        this.isValidResponsable = false;
      }
    }
  }

  private checkInfoResponsable1() {
    return !this.responsables.userResponsable.lastName ||
      !this.responsables.userResponsable.firstName ||
      !this.sexRespValidator ||
      (!this.responsables.userResponsable.email &&
        !this.phoneResponsable1) ||
      !this.responsables.userResponsable.address.address1 ||
      !this.responsables.userResponsable.address.zipCode ||
      !this.responsables.userResponsable.address.city;
  }

  private checkInfoResponsable2() {
    return !this.responsables.userSecondResponsable.lastName
      || !this.responsables.userSecondResponsable.firstName
      || !this.sexRespValidator
      || (!this.responsables.userSecondResponsable.email && !this.phoneResponsable2)
      || !this.responsables.userSecondResponsable.address.address1
      || !this.responsables.userSecondResponsable.address.zipCode
      || !this.responsables.userSecondResponsable.address.city;
  }

  private save() {
    this.userWebservice.sendGlobalRegister(this.globalRegister).pipe(takeUntil(this.destroyed))
      .subscribe({
        next: () => {
          this.validationService.setResponseState(true);
          this.isLoading = false;
          this.alertService.clear(this.alertStep3);
          this.alertService.success(
            'Pré-inscription enregistrée. Merci de vous connecter afin de finaliser votre inscription.', this.alertStep3
          );

          this.handleRedirection();
        },
        error: error => {
          this.isLoading = false;
          this.formPlaceSubmitted = false;
          this.hasValidatedAll = false;
          this.validationService.setResponseState(false);
          this.alertService.clear(this.alertStep3);
          this.alertService.error(error.message, this.alertStep3);
        }, complete: () => {
          this.formGroupStepPlace.controls.nameCenter.clearValidators();
          this.treeDataService.reset();
        }
      });
  }

  private handleRedirection() {
    setTimeout(() => { // Permet le visionnage du message d'enregistrement réussi
      if (!this.isEventAdd) {
        if (this.isAddYoung) {
          this.router.navigate(['/children']);
        } else {
          const phoneNumber = this.isOlderThan17 ? this.userToRegister.userRegistered.phone
            : this.responsables.userResponsable.phone;
          this.router.navigate(['/success'], {state: {phoneNumber}});
        }
      } else {
        this.router.navigate(['/info'], {queryParams: {tab: 'summary'}});
      }
    }, this.timeout);
  }

  private registerPlaceEvent(listOfSelectedPlaceAndEvent: Map<number, Event[]>, placeEvent) {
    if (listOfSelectedPlaceAndEvent && listOfSelectedPlaceAndEvent.size > 0) {
      listOfSelectedPlaceAndEvent.forEach((value: Event[], key: number) => {
        if (!this.eventToRegister.ListOfPlaceEvent) {
          this.eventToRegister.ListOfPlaceEvent = [];
        }
        if (value && value.length > 0) {
          placeEvent = new PlaceEventDTO();
          placeEvent.idPlace = key;
          placeEvent.events = value;

          this.eventToRegister.ListOfPlaceEvent.push(placeEvent);
        }
      });
      this.hasValidatedAll = true;
    } else {
      this.hasValidatedAll = false;
      this.formPlaceSubmitted = false;
      this.isLoading = false;
      this.alertService.clear(this.alertStep3);
      this.alertService.error('La liste des événements est vide.', this.alertStep3);
    }
  }

  private stepYoung() {
    this.formYoungSubmitted = true;
    if (!this.formGroupStepYoung.invalid && this.isPhoneStep1IfAdult && this.isMailStep1IfAdult && !this.isInvalidDisciplineDIfficulteScolaire) {
      this.userToRegister.userRegistered = this.user;
      this.userToRegister.youngRegistered = this.young;
      this.userToRegister.schoolLevelYoung = this.schoolLevel;
      this.userToRegister.addressYoung = this.address;
      this.userToRegister.listOfYoungPDifficulteScolaireIds = this.fStep1Young.scolarDifficulty.value;
      this.userToRegister.listOfYoungPDisciplineDifficulteScolaireIds = this.fStep1Young.disciplineScolarDifficulty.value;

      this.youngCreationDataService.changeCurrentUser(this.user);

      if (!this.formGroupStepResp) {
        this.initformGroupStepResp();
      }
      this.nextPage = true;
      this.stepPlaceWithoutResp();
      this.refreshCurrentUser();
      if (this.userWebservice.currentUser) {
        const resp = new RegisterResponsableDTO();
        resp.userResponsable = this.userWebservice.currentUser;
        resp.userFamily = new RelationUser();
        this.responsables = resp;
      }
    }
  }

  private handleFormGroupChanges() {
    // Ajout du champ telephone, required si l'inscrit est >= 18 ans
    this.formGroupStepYoung.controls.birthday.valueChanges.pipe(takeUntil(this.destroyed)).subscribe((value: Date) => {
      const dateMajeur = new Date();
      dateMajeur.setFullYear(dateMajeur.getFullYear() - 18);

      if (value) {
        const rawBirthday = this.formatService.formatDate(value);
        const isCompleteBirthday = /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(rawBirthday);

        if (isCompleteBirthday && dateMajeur >= value) {
          this.isPhoneStep1IfAdult = (this.fStep1Young.phone.value && this.fStep1Young.phone.value !== '');
          this.isMailStep1IfAdult = (this.fStep1Young.mail.value && this.fStep1Young.mail.value !== '');
          this.isOlderThan17 = true;
          this.formGroupStepYoung.get('phone').setValidators(this.phoneValidators.concat(Validators.required));
          this.formGroupStepYoung.get('mail').setValidators(this.mailValidators.concat(Validators.required));
          this.formGroupStepYoung.get('phone').updateValueAndValidity();
          this.formGroupStepYoung.get('mail').updateValueAndValidity();
        } else {
          this.isPhoneStep1IfAdult = true;
          this.isMailStep1IfAdult = true;
          this.isOlderThan17 = false;
          this.formGroupStepYoung.get('phone').setValidators(this.phoneValidators);
          this.formGroupStepYoung.get('mail').setValidators(this.mailValidators);
          this.formGroupStepYoung.get('phone').updateValueAndValidity();
          this.formGroupStepYoung.get('mail').reset();
          this.user.email = null;
          this.blockNext = false;
          this.alertService.clear(this.alertStep1Email);
          this.inscriptionService.setAlreadyExistsYoungEmail(false);
          this.inscriptionService.resetYoungEmail();
          this.formGroupStepYoung.get('mail').updateValueAndValidity();
        }


        this.youngCreationDataService.resetRegisterEvent();
      }
    });

    // Complete les objets au fur et a mesure
    this.handleChangesWithTrim('lastName', this.user);
    this.handleChangesWithTrim('firstName', this.user);
    this.handleChangesWithTrim('address1', this.address);
    this.handleChangesWithTrim('zipCode', this.address);
    this.handleChangesWithTrim('city', this.address);

    this.handleChanges('birthday', this.user);
    this.handleChanges('schoolLevel', this.schoolLevel, 'idPSchoolLevel');
    this.handleChanges('etablissement', this.schoolLevel, 'schoolName');
    this.handleChanges('numberBrother', this.young);
    this.handleChanges('numberSister', this.young);
    this.handleChanges('childs', this.young);
    this.handleChanges('handicap', this.young);
    this.handleChanges('medicalObservation', this.young);

    this.formGroupStepYoung.controls.mail.valueChanges.pipe(takeUntil(this.destroyed))
      .subscribe(value => {
        if (value && value !== '' && value !== undefined) {
          if (this.mailRegex.test(value)) {
            this.user.email = this.formatService.trim(value);
            this.inscriptionService.setYoungEmail(this.user.email);
            this.blockNext = true;
            this.cancelMailExists.next();
            this.userWebservice.postCheckEmailExist(value)
              .pipe(debounceTime(200), takeUntil(this.cancelMailExists), this.untilDestroyed())
              .subscribe({
                next: (res: boolean) => {
                  this.handleEmailExistsAlreadyError(res);
                }
              });
          }
        } else {
          this.user.email = null;
          this.inscriptionService.resetYoungEmail();
          if (value === '') {
            this.blockNext = false;
            this.alertService.clear(this.alertStep1Email);
            this.isMailStep1IfAdult = true;
          }
        }
        this.checkPhoneOrMailStep1IfAdult();
      });

    this.formGroupStepYoung.controls.phone.valueChanges.pipe(takeUntil(this.destroyed))
      .subscribe((value: string) => {
        if (value && value !== '' && value !== undefined) {
          if (this.phoneRegex.test(value)) {
            this.tronqPhoneNumber(value);
            this.blockNext = true;
            this.handlePhoneExists();
          }
        } else {
          this.user.phone = null;
          this.blockNext = false;
          this.alertService.clear(this.alertStep1Phone);
          this.inscriptionService.resetYoungPhone();
        }
        this.checkPhoneOrMailStep1IfAdult();
      });
  }

  private handleChanges(field: string, destinationObject: unknown, destinationField?: string) {
    this.formGroupStepYoung.controls[field].valueChanges.pipe(takeUntil(this.destroyed)).subscribe(value => {
      if (value) {
        if (destinationField) {
          destinationObject[destinationField] = value;
        } else {
          destinationObject[field] = value;
        }
      }
    });
  }

  private handleChangesWithTrim(
    field: string,
    destinationObject: unknown,
    destinationField?: string) {
    this.formGroupStepYoung.controls[field].valueChanges.pipe(takeUntil(this.destroyed)).subscribe(value => {
      if (value) {
        if (destinationField) {
          destinationObject[destinationField] = this.formatService.trim(value);
        } else {
          destinationObject[field] = this.formatService.trim(value);
        }
      }
    });
  }

  private initFormGroupStep1() {
    // Step 1
    this.formGroupStepYoung = this.fb.group({
      lastName: [null, Validators.required],
      firstName: [null, Validators.required],
      sexMale: [null, Validators.required],
      sexFemale: [null, Validators.required],
      birthday: [null, Validators.required],
      phoneCode: null,
      phone: [null, this.phoneValidators],
      mail: [null, this.mailValidators],
      numberBrother: null,
      numberSister: null,
      childs: null,
      schoolLevel: [null, Validators.required],
      schoolLevelYear: null,
      scolarDifficulty: [[]],
      disciplineScolarDifficulty: [[]],
      etablissement: null,
      handicap: false,
      medicalObservation: null,
      address1: [null, Validators.required],
      zipCode: [null, Validators.required],
      city: [null, Validators.required]
    });
  }

  private setFormResponsable(user: User, relationUser: RelationUser, phoneCode: number, phoneResponsable: string) {
    this.formGroupStepResp.controls.lastName.setValue(user.lastName);
    this.formGroupStepResp.controls.firstName.setValue(user.firstName);
    this.formGroupStepResp.controls.sex.setValue(user.sex);
    this.formGroupStepResp.controls.familySituation.setValue(relationUser.familySituation);
    this.formGroupStepResp.controls.job.setValue(relationUser.job);
    this.formGroupStepResp.controls.mail.setValue(user.email);
    this.formGroupStepResp.controls.phoneCode.setValue(phoneCode);
    this.formGroupStepResp.controls.phone.setValue(phoneResponsable);
    this.formGroupStepResp.controls.address1.setValue(user.address.address1);
    this.formGroupStepResp.controls.zipCode.setValue(user.address.zipCode);
    this.formGroupStepResp.controls.city.setValue(user.address.city);
  }

  private validateAndSetPhoneNumberResp(value: string, isResp2: boolean = false) {
    this.getValuePhoneResp(value, isResp2);

    if (this.testPhoneNumber(value)) {
      let telBdd = value.length === 10 ? value.slice(1) : value;
      telBdd = `${this.phoneRegionCodeList[this.formGroupStepResp.controls.phoneCode.value]}${telBdd}`;
      isResp2 ? this.inscriptionService.setResp2Phone(telBdd) : this.inscriptionService.setResp1Phone(telBdd);

      if (this.user.phone === telBdd) {
        this.alertService.clear(this.alertStep2);
        this.alertService.error(`Ce responsable légal doit avoir un numéro de téléphone différent de celui du jeune.`,
          this.alertStep2);
      } else {
        // S'il y'a deux resp
        this.handleResPhonesNumber(telBdd, isResp2);
      }
      this.handlePhoneErrors(isResp2, telBdd);
    } else {
      this.alertService.clear(this.alertStep2);
      this.alertService.clear(this.alertStep2Phone);
      this.inscriptionService.resetRespPhone(!isResp2);
    }
  }

  private handlePhoneErrors(isResp2: boolean, telBdd: string) {
    this.handlePhoneExistsErrors(telBdd, isResp2);
  }

  private validateAndSetMailResp(value: string, isResp2: boolean = false) {
    this.getValueMailResp(isResp2, value?.trim() ?? '');

    if (value && value !== '' && value !== undefined) {
      if (this.mailRegex.test(value)) {
        if (this.user.email === value) {
          this.alertService.clear(this.alertStep2);
          this.alertService.error(`Ce responsable légal doit avoir une adresse mail différente de celle du jeune.`,
            this.alertStep2);
        } else {
          this.handleTwoRespErrors();
          this.handleMailErrors(isResp2, value);
        }
      }
    } else {
      this.inscriptionService.resetRespEmail(!isResp2);
      this.alertService.clear(this.alertStep2Email);
    }
  }

  private handleMailErrors(isResp2: boolean, value: string) {
    this.handleMailExistsErrors(value, isResp2);
  }

  private initSchoolLevel() {
    this.paramWebservice.getAllSchoolLevels().pipe(takeUntil(this.destroyed)).subscribe(
      (content: PSchoolLevel[]) => {
        content.forEach(schoolLevel => {
          const option = new FormSelectItem();
          option.id = schoolLevel.id;
          option.name = schoolLevel.label;
          this.schoolLevelForSelect.push(option);
        });
      });
  }

  private initScolarDifficulty() {
    this.paramWebservice.getAllScolarDifficulty().pipe(takeUntil(this.destroyed)).subscribe(
      (content: PScolarDifficulty[]) => {
        content.forEach(scolarDifficulty => {
          const option = new FormSelectItem();
          option.id = scolarDifficulty.id;
          option.name = scolarDifficulty.label;
          this.scolarDifficultyForSelect.push(option);
        });
      });
  }

  private initDisciplineScolarDifficulty() {
    this.paramWebservice.getAllDisciplineScolarDifficulty().pipe(takeUntil(this.destroyed)).subscribe(
      (content: PDisciplineScolarDifficulty[]) => {
        content.forEach(disciplineScolarDifficulty => {
          const option = new FormSelectItem();
          option.id = disciplineScolarDifficulty.id;
          option.name = disciplineScolarDifficulty.label;
          this.disciplineScolarDifficultyForSelect.push(option);
        });
      });
  }
}
